import { useState, useEffect } from 'react';
import { Zap, TrendingUp, CheckCircle, Clock, AlertCircle, Loader2, RefreshCw, BarChart2, Star } from 'lucide-react';
import apiClient from '@/services/api/apiClient';

interface EngagementData {
  score: number;
  mqlReadinessLevel: 'NOT_READY' | 'WARMING' | 'NEAR_READY' | 'READY';
  mqlReadyAt?: string;
  breakdown?: {
    stageDepth: number;
    eventRichness: number;
    selfAssessment: number;
    calls: number;
    total: number;
    stagesEngaged: number;
    eventCounts: Record<string, number>;
  };
  selfAssessedBand?: string;
  selfAssessedAxis?: string;
  stagesEngaged?: number;
  totalEvents?: { opens: number; clicks: number; downloads: number; formSubmits: number; revisits: number };
  updatedAt?: string;
}

const LEVEL_CONFIG = {
  NOT_READY:  { label: 'Not Ready',   color: 'text-muted-foreground',                      bg: 'bg-muted/50',         border: 'border-border',         icon: Clock,         ring: 'bg-muted'       },
  WARMING:    { label: 'Warming Up',  color: 'text-amber-600 dark:text-amber-400',          bg: 'bg-amber-50 dark:bg-amber-950/20',  border: 'border-amber-200 dark:border-amber-800', icon: TrendingUp, ring: 'bg-amber-400'    },
  NEAR_READY: { label: 'Near Ready',  color: 'text-blue-600 dark:text-blue-400',            bg: 'bg-blue-50 dark:bg-blue-950/20',    border: 'border-blue-200 dark:border-blue-800',   icon: Zap,        ring: 'bg-blue-500'     },
  READY:      { label: 'MQL Ready!',  color: 'text-emerald-600 dark:text-emerald-400',      bg: 'bg-emerald-50 dark:bg-emerald-950/20', border: 'border-emerald-200 dark:border-emerald-800', icon: Star, ring: 'bg-emerald-500' },
};

interface Props {
  contactId: string;
  compact?: boolean;
  onReady?: () => void;
}

export default function EngagementScoreWidget({ contactId, compact = false, onReady }: Props) {
  const [data, setData]       = useState<EngagementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get(`/api/engage/score/${contactId}`);
      setData(d);
      if (d.mqlReadinessLevel === 'READY') onReady?.();
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  const recompute = async () => {
    setLoading(true);
    try {
      await apiClient.post(`/api/engage/score/${contactId}/recompute`);
      await load();
    } catch { setLoading(false); }
  };

  useEffect(() => { load(); }, [contactId]);

  if (loading) return (
    <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" />Computing engagement score…
    </div>
  );

  const score = data?.score ?? 0;
  const level = data?.mqlReadinessLevel ?? 'NOT_READY';
  const cfg   = LEVEL_CONFIG[level];
  const Icon  = cfg.icon;

  // Compact mode — just the badge + score bar (for sidebar)
  if (compact) return (
    <div className={`rounded-xl border p-3 space-y-2 ${cfg.bg} ${cfg.border}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
          <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold text-foreground">{score}</span>
          <span className="text-xs text-muted-foreground">/100</span>
          <button onClick={recompute} className="p-0.5 rounded hover:bg-muted ml-1 text-muted-foreground">
            <RefreshCw className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>
      {/* Score bar */}
      <div className="h-1.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${cfg.ring}`} style={{ width: `${score}%` }} />
      </div>
      {/* Milestone markers */}
      <div className="flex justify-between text-[9px] text-muted-foreground">
        <span>0</span><span className="text-amber-500">30 warm</span><span className="text-blue-500">55 near</span><span className="text-emerald-500">75 MQL</span>
      </div>
      {data?.selfAssessedBand && (
        <p className="text-[10px] text-muted-foreground">Self-assessed: <strong>{data.selfAssessedBand}</strong></p>
      )}
    </div>
  );

  // Full mode
  return (
    <div className={`rounded-xl border overflow-hidden ${cfg.bg} ${cfg.border}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${cfg.ring} text-white`}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</p>
            <p className="text-xs text-muted-foreground">ABM maturity score</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-3xl font-bold text-foreground">{score}</p>
            <p className="text-xs text-muted-foreground">/100</p>
          </div>
          <button onClick={recompute} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" title="Recompute">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Score bar with milestones */}
      <div className="px-4 pb-3 space-y-1">
        <div className="relative h-2 rounded-full bg-border overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ${cfg.ring}`} style={{ width: `${score}%` }} />
          {/* Milestone markers */}
          {[30, 55, 75].map(m => (
            <div key={m} className="absolute top-0 h-full w-px bg-white/40" style={{ left: `${m}%` }} />
          ))}
        </div>
        <div className="flex justify-between text-[10px]">
          <span className="text-muted-foreground">Cold</span>
          <span className="text-amber-500 font-medium">30 Warming</span>
          <span className="text-blue-500 font-medium">55 Near</span>
          <span className="text-emerald-500 font-medium">75 MQL ✓</span>
        </div>
      </div>

      {/* Score breakdown */}
      {data?.breakdown && (
        <div className="px-4 pb-3">
          <button onClick={() => setExpanded(v => !v)}
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors pb-2">
            <span className="flex items-center gap-1"><BarChart2 className="h-3 w-3" />Score breakdown</span>
            <span>{expanded ? '▲' : '▼'}</span>
          </button>
          {expanded && (
            <div className="space-y-2">
              {[
                { label: 'Stage depth',      value: data.breakdown.stageDepth,     max: 40, color: 'bg-violet-500', desc: `${data.breakdown.stagesEngaged} stages engaged` },
                { label: 'Event richness',   value: data.breakdown.eventRichness,  max: 30, color: 'bg-blue-500',   desc: 'opens, clicks, downloads, forms' },
                { label: 'Self-assessment',  value: data.breakdown.selfAssessment, max: 20, color: 'bg-amber-500',  desc: data?.selfAssessedBand ? `Self-assessed: ${data.selfAssessedBand}` : 'Not completed' },
                { label: 'Calls completed',  value: data.breakdown.calls,          max: 10, color: 'bg-emerald-500',desc: `${data.breakdown.calls / 5} / 2 calls done` },
              ].map(b => (
                <div key={b.label}>
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>{b.label} <span className="opacity-60">({b.desc})</span></span>
                    <span className="font-mono font-bold text-foreground">{b.value}/{b.max}</span>
                  </div>
                  <div className="h-1 rounded-full bg-border overflow-hidden">
                    <div className={`h-full rounded-full ${b.color}`} style={{ width: `${(b.value / b.max) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event stats */}
      {data?.totalEvents && (
        <div className="px-4 pb-3">
          <div className="grid grid-cols-5 gap-1 text-center">
            {[
              { label: 'Opens',     value: data.totalEvents.opens     },
              { label: 'Clicks',    value: data.totalEvents.clicks    },
              { label: 'Downloads', value: data.totalEvents.downloads },
              { label: 'Forms',     value: data.totalEvents.formSubmits },
              { label: 'Revisits',  value: data.totalEvents.revisits  },
            ].map(e => (
              <div key={e.label} className="rounded-lg bg-background/60 border border-border p-1.5">
                <p className="text-sm font-bold text-foreground">{e.value}</p>
                <p className="text-[9px] text-muted-foreground">{e.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MQL ready state */}
      {level === 'READY' && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div>
              <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400">MQL Ready</p>
              <p className="text-[10px] text-emerald-600 dark:text-emerald-500">
                Score reached 75+ · Ready for sales handoff
                {data?.mqlReadyAt && ` · ${new Date(data.mqlReadyAt).toLocaleDateString()}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Self-assessment */}
      {data?.selfAssessedBand && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-background/60 border border-border text-xs">
            <span className="text-muted-foreground">Contact self-assessed:</span>
            <span className="font-bold text-foreground">{data.selfAssessedBand}</span>
            {data.selfAssessedAxis && <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">{data.selfAssessedAxis}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
